import { Navigate } from "react-router-dom";
import MessagesPage from "../domains/notifications/pages/MessagesPage";
import ForgottenPasswordPage from "../domains/auth/pages/ForgottenPasswordPage";
import ParentalApprovalPage from "../domains/forms/pages/ParentalApprovalPage";
import SchoolRegistrationConfirmationPage from "../domains/forms/pages/SchoolRegistrationConfirmationPage";
import AccountWaitingPage from "../domains/auth/pages/AccountWaitingPage";
import VideoInscrierePage from "../domains/auth/pages/VideoInscrierePage";
import ProtocolPage from "../domains/auth/pages/ProtocolPage";
import CerereCodPage from "../domains/auth/pages/CerereCodPage";
import VerificareCodPage from "../domains/auth/pages/VerificareCodPage";
import InregistrareCodPage from "../domains/auth/pages/InregistrareCodPage";
import InregistrareElevCodPage from "../domains/auth/pages/InregistrareElevCodPage";
import { PAS_VIDEO_ACTIV } from "../domains/auth/utils/video-flag";
import { PROTOCOL_DESCARCARE_ACTIV } from "../domains/auth/utils/protocol-flag";
import { CERE_RECIPIENTE_ACTIV } from "../domains/recipients/utils/recipiente-flag";
import { AUTENTIFICARE_COD_ACTIVA } from "../domains/auth/utils/otp-flag";
import { AUTH_PAROLA_ACTIVA } from "../domains/auth/utils/parola-flag";
import { INREGISTRARE_COD_ACTIV } from "../domains/auth/utils/inregistrare-cod-flag";
import CodScoalaPage from "../domains/auth/pages/CodScoalaPage";
import { INTRARE_COD_SCOALA_ACTIVA } from "../domains/auth/utils/intrare-cod-flag";
import { homePathForRole } from "../domains/auth/utils/user-home-page";
import AcasaPage from "../domains/home/pages/AcasaPage";
import { NotificationsReportPage } from '../domains/notifications/pages/NotificationsReportPage';
import RegisterPage from "../domains/auth/pages/RegisterPage";
import PupilReenrollPage from "../domains/auth/pages/PupilReenrollPage";
import { isFeatureEnabled } from '../core/config/feature-flags';
import { SchoolRequestsReportPage } from '../domains/school-requests/pages/SchoolRequestsReportPage';
import SchoolRequestFormPage from '../domains/school-requests/pages/SchoolRequestFormPage';
import WasteCollectionFormPage from '../domains/waste-collection/pages/WasteCollectionFormPage';
import SchoolPointsPage from '../domains/tickets/pages/SchoolPointsPage';
import TicketTiersAdminPage from '../domains/tickets/pages/TicketTiersAdminPage';
import CereRecipientePage from '../domains/recipients/pages/CereRecipientePage';
import RecipienteAdminPage from '../domains/recipients/pages/RecipienteAdminPage';
import TicketRedemptionsReportPage from '../domains/tickets/pages/TicketRedemptionsReportPage';
import SchoolDetailsFormPage from '../domains/schools/pages/SchoolDetailsFormPage';
import { CollaboratorLinksPage } from '../domains/schools/pages/CollaboratorLinksPage';
import CollabActivityPage from '../domains/collab/pages/CollabActivityPage';
import { SchoolsReportPage } from '../domains/schools/pages/SchoolsReportPage';
import { SchoolsGraphicReportPage } from '../domains/schools/pages/SchoolsGraphicReportPage';
import { StudentsLeaderboardReportPage } from '../domains/students/pages/StudentsLeaderboardReportPage';
import { UsersReportPage } from '../domains/users/pages/UsersReportPage';
import { WasteOrdersReportPage } from '../domains/waste-collection/pages/WasteOrdersReportPage';
import { RejectedWasteOrdersReportPage } from '../domains/waste-collection/pages/RejectedWasteOrdersReportPage';
import { EditionStatusReportPage } from '../domains/schools/pages/EditionStatusReportPage';
import { LessonAnswersReportPage } from '../domains/lessons/features/reporting/LessonAnswersReportPage';
import PracticalDetailsPage from '../domains/lessons/features/practical/PracticalDetailsPage';
import PracticalEvidencePage from '../domains/lessons/features/practical/PracticalEvidencePage';
import { LessonSystemRouter } from '../domains/lessons/routing';
import { LessonType } from '../domains/lessons/core/types';
import ProfilePage from "../domains/forms/pages/ProfilePage";
import PasswordChangePage from "../domains/auth/pages/PasswordChangePage";
import NewPasswordPage from "../domains/auth/pages/NewPasswordPage";
import LoginPage, { AutentificareEmailPage } from "../domains/auth/pages/LoginPage";
import HomePage from "../pages/HomePage";
import { createContext, ReactNode } from "react";
import type { RoutesProviderValue, Role, NavLinksType } from './routes-provider.types';

interface RoutesProviderProps {
    children: ReactNode;
}

const RoutesProviderContext = createContext<RoutesProviderValue | null>(null);
const { Provider } = RoutesProviderContext;

const RoutesProvider = ({ children }: RoutesProviderProps): JSX.Element => {
    const ROLES = Object.freeze({
        DBEST: "DBEST",
        ADMIN: "ADMIN",
        PROFESOR: "PROFESOR",
        ELEV: "ELEV",
        LOGISTIC: "LOGISTIC",
    });

    const ROLES_PRESENTATION_NAME = Object.freeze({
        [ROLES.DBEST]: "Programator",
        [ROLES.ADMIN]: "Administrator Ecotic",
        [ROLES.LOGISTIC]: "Staff Ecotic",
        [ROLES.PROFESOR]: "Profesor",
        [ROLES.ELEV]: "Elev",
    });

    const PAGES = Object.freeze({
        HOME: "HOME",
        ACASA: "ACASA", // «Acasa» pentru PROFESOR si ELEV (pagina statica); HOME de mai sus e pagina publica
        LOGIN: "LOGIN", // Unified login for all users
        TEACHER_AUTH: "TEACHER_AUTH", // Deprecated - use LOGIN instead
        TEACHER_REGISTER: "TEACHER_REGISTER",
        PUPIL_AUTH: "PUPIL_AUTH", // Deprecated - use LOGIN instead
        PUPIL_REGISTER: "PUPIL_REGISTER",
        PUPIL_REENROLL: "PUPIL_REENROLL",
        FORGOTTEN_PASSWORD: "FORGOTTEN_PASSWORD",
        NEW_PASS_FOR_FORGOTTEN_PASSWORD: "NEW_PASS_FOR_FORGOTTEN_PASSWORD",

        LESSON_QUIZ_LISTING: "LESSON_QUIZ_LISTING",
        LESSON_QUIZ_CREATION: "LESSON_QUIZ_CREATION",

        HOMEWORK_LISTING: "HOMEWORK_LISTING",
        HOMEWORK_CREATION: "HOMEWORK_CREATION",

        PRACTICAL_ACTIVITY_LISTING: "PRACTICAL_ACTIVITY",
        PRACTICAL_ACTIVITY_CREATION: "PRACTICAL_ACTIVITY_CREATION",
        // B3: „Detalii" si „Încarcă poze" (intrarile din NAV_LINKS_V2 vin in Task 9/10).
        PRACTICAL_ACTIVITY_DETAILS: "PRACTICAL_ACTIVITY_DETAILS",
        PRACTICAL_ACTIVITY_EVIDENCE: "PRACTICAL_ACTIVITY_EVIDENCE",

        // Provocari pentru scoli (2026-10-02): lista, editor, „Detalii" si „Încarcă dovezi".
        SCHOOL_CHALLENGE_LISTING: "SCHOOL_CHALLENGE_LISTING",
        SCHOOL_CHALLENGE_CREATION: "SCHOOL_CHALLENGE_CREATION",
        SCHOOL_CHALLENGE_DETAILS: "SCHOOL_CHALLENGE_DETAILS",
        SCHOOL_CHALLENGE_EVIDENCE: "SCHOOL_CHALLENGE_EVIDENCE",

        MONTHLY_CHALLENGES_LISTING: "MONTHLY_CHALLENGES_LISTING",
        MONTHLY_CHALLENGES_CREATION: "MONTHLY_CHALLENGES_CREATION",

        CLASS_EXERCISE_LISTING: "CLASS_EXERCISE_LISTING",
        CLASS_EXERCISE_CREATION: "CLASS_EXERCISE_CREATION",
        CLASS_EXERCISE_COMPLETION: "CLASS_EXERCISE_COMPLETION",
        CLASS_EXERCISE_COMPLETION_PREVIEW: "CLASS_EXERCISE_COMPLETION_PREVIEW",

        // DAILY_CHALLENGES_LISTING: "DAILY_CHALLENGES_LISTING",
        // DAILY_CHALLENGES_CREATION: "DAILY_CHALLENGES_CREATION",
        // DAILY_CHALLENGES_CREATION_REDIRECT: "DAILY_CHALLENGES_CREATION_REDIRECT",

        WASTE_COLLECTION_FORM: "WASTE_COLLECTION_FORM",
        SCHOOL_POINTS: "SCHOOL_POINTS",
        TICKET_TIERS_ADMIN: "TICKET_TIERS_ADMIN",
        CERE_RECIPIENTE: "CERE_RECIPIENTE",
        RECIPIENTE_ADMIN: "RECIPIENTE_ADMIN",
        TICKET_REDEMPTIONS_REPORT: "TICKET_REDEMPTIONS_REPORT",
        WASTE_COLLECTION_TABLE: "WASTE_COLLECTION_TABLE",
        WASTE_COLLECTION_REJECTED_TABLE: "WASTE_COLLECTION_REJECTED_TABLE",
        MESSAGES: "MESSAGES",
        PROFILE: "PROFILE",
        PASSWORD_CHANGE: "PASSWORD_CHANGE",
        ACCOUNTS_AUTHORIZATION: "ACCOUNTS_AUTHORIZATION",
        ACCOUNT_UNAUTHORIZED_TEACHER: "ACCOUNT_UNAUTHORIZED_TEACHER",
        VIDEO_INSCRIERE: "VIDEO_INSCRIERE",
        PROTOCOL_INSCRIERE: "PROTOCOL_INSCRIERE",
        CERERE_COD: "CERERE_COD",
        LOGIN_EMAIL: "LOGIN_EMAIL",
        VERIFICARE_COD: "VERIFICARE_COD",
        INTRARE_COD_SCOALA: "INTRARE_COD_SCOALA",
        LESSON_COMPLETION: "LESSON_COMPLETION",
        LESSON_COMPLETION_PREVIEW: "LESSON_COMPLETION_PREVIEW",

        HOMEWORK_COMPLETION: "HOMEWORK_COMPLETION",
        HOMEWORK_COMPLETION_PREVIEW: "HOMEWORK_COMPLETION_PREVIEW",

        PRACTICAL_ACTIVITY_COMPLETION: "PRACTICAL_ACTIVITY_COMPLETION",
        PRACTICAL_ACTIVITY_COMPLETION_PREVIEW: "PRACTICAL_ACTIVITY_COMPLETION_PREVIEW",

        MONTHLY_CHALLENGES_COMPLETION: "MONTHLY_CHALLENGES_COMPLETION",
        MONTHLY_CHALLENGES_COMPLETION_PREVIEW: "MONTHLY_CHALLENGES_COMPLETION_PREVIEW",

        // DAILY_CHALLENGES_COMPLETION: "DAILY_CHALLENGES_COMPLETION",
        // DAILY_CHALLENGES_COMPLETION_PREVIEW: "DAILY_CHALLENGES_COMPLETION_PREVIEW",

        SCHOOLS_REPORT: "SCHOOLS_REPORT",
        SCHOOLS_GRAPHIC_REPORT: "SCHOOLS_GRAPHIC_REPORT",
        TOP_ELEVI_REPORT: "TOP_ELEVI_REPORT",
        COMPLETED_QUIZZES: "COMPLETED_QUIZZES",
        NOTIFICATIONS_REPORT: "NOTIFICATIONS_REPORT",
        SCHOOL_REQUEST_FORM: "SCHOOL_REQUEST_FORM",
        SCHOOL_FORM_V2: "SCHOOL_FORM_V2",
        PARENTAL_APPROVAL: "PARENTAL_APPROVAL",
        FINAL_REPORT: "FINAL_REPORT",
        PUPILS_REPORT: "PUPILS_REPORT",
        USERS_REPORT: "USERS_REPORT",
        SCHOOL_REGISTRATION_CONFIRMATION: "SCHOOL_REGISTRATION_CONFIRMATION",
        COLLABORATOR_LINKS: "COLLABORATOR_LINKS",
        COLLAB_ACTIVITY: "COLLAB_ACTIVITY",
    });

    const PATHS = Object.freeze({
        [PAGES.HOME]: "/informatii-despre-scoala-ecoterrienilor",
        [PAGES.ACASA]: "/acasa",
        [PAGES.LOGIN]: "/autentificare",
        [PAGES.TEACHER_AUTH]: "/profesor/autentificare", // Deprecated - redirects to /autentificare
        [PAGES.TEACHER_REGISTER]: "/profesor/inregistrare",
        [PAGES.PUPIL_AUTH]: "/elev/autentificare", // Deprecated - redirects to /autentificare
        [PAGES.PUPIL_REGISTER]: "/elev/inregistrare",
        [PAGES.PUPIL_REENROLL]: "/elev/reinscriere",
        [PAGES.FORGOTTEN_PASSWORD]: "/am-uitat-parola",
        [PAGES.NEW_PASS_FOR_FORGOTTEN_PASSWORD]: "/confirmare-resetare/:token?",
        [PAGES.LESSON_QUIZ_LISTING]: "/lectii",
        [PAGES.LESSON_QUIZ_CREATION]: "/lectii/editare/:id?",

        [PAGES.HOMEWORK_LISTING]: "/exercitii-pentru-acasa",
        [PAGES.HOMEWORK_CREATION]: "/exercitii-pentru-acasa/editare/:id?",

        [PAGES.PRACTICAL_ACTIVITY_LISTING]: "/activitati-practice",
        [PAGES.PRACTICAL_ACTIVITY_CREATION]: "/activitati-practice/editare/:id?",
        // B3: aceleasi cai ca practicalPaths() din domains/lessons/features/practical/practical-evidence.ts.
        [PAGES.PRACTICAL_ACTIVITY_DETAILS]: "/activitati-practice/id/:id/detalii",
        [PAGES.PRACTICAL_ACTIVITY_EVIDENCE]: "/activitati-practice/id/:id/incarca",

        // Aceleasi cai ca practicalPaths(id, 'SCHOOL_CHALLENGE').
        [PAGES.SCHOOL_CHALLENGE_LISTING]: "/provocari-scoli",
        [PAGES.SCHOOL_CHALLENGE_CREATION]: "/provocari-scoli/editare/:id?",
        [PAGES.SCHOOL_CHALLENGE_DETAILS]: "/provocari-scoli/id/:id/detalii",
        [PAGES.SCHOOL_CHALLENGE_EVIDENCE]: "/provocari-scoli/id/:id/incarca",

        [PAGES.MONTHLY_CHALLENGES_LISTING]: "/provocari",
        [PAGES.MONTHLY_CHALLENGES_CREATION]: "/provocari/editare/:id?",

        [PAGES.CLASS_EXERCISE_LISTING]: "/exercitii-pentru-clasa",
        [PAGES.CLASS_EXERCISE_CREATION]: "/exercitii-pentru-clasa/editare/:id?",
        [PAGES.CLASS_EXERCISE_COMPLETION]: "/completare-exercitiu-pentru-clasa/id/:id",
        [PAGES.CLASS_EXERCISE_COMPLETION_PREVIEW]: "/completare-exercitiu-pentru-clasa/id/:id/preview",

        // [PAGES.DAILY_CHALLENGES_LISTING]: "/provocari-zilnice",
        // [PAGES.DAILY_CHALLENGES_CREATION]: "/provocari-zilnice/editare/:id?",
        // [PAGES.DAILY_CHALLENGES_CREATION_REDIRECT]: "/provocari-zilnice/editare",

        [PAGES.WASTE_COLLECTION_FORM]: "/plasare-comanda",
        [PAGES.SCHOOL_POINTS]: "/puncte-si-tichete",
        [PAGES.TICKET_TIERS_ADMIN]: "/admin/niveluri-tichete",
        [PAGES.CERE_RECIPIENTE]: "/cere-recipiente",
        [PAGES.RECIPIENTE_ADMIN]: "/admin/recipiente",
        [PAGES.TICKET_REDEMPTIONS_REPORT]: "/rapoarte/tichete",
        [PAGES.WASTE_COLLECTION_TABLE]: "/rapoarte/comenzi",
        [PAGES.WASTE_COLLECTION_REJECTED_TABLE]: "/rapoarte/comenzi-respinse",
        [PAGES.MESSAGES]: "/mesaje",
        [PAGES.PROFILE]: "/profil",
        [PAGES.PASSWORD_CHANGE]: "/schimba-parola",
        [PAGES.ACCOUNTS_AUTHORIZATION]: "/rapoarte/inscrieri",
        [PAGES.ACCOUNT_UNAUTHORIZED_TEACHER]: "/cont-in-asteptare/profesor",
        [PAGES.VIDEO_INSCRIERE]: "/cont-in-asteptare/material-video",
        [PAGES.PROTOCOL_INSCRIERE]: "/cont-in-asteptare/protocol",
        [PAGES.CERERE_COD]: "/cod-de-acces",
        [PAGES.LOGIN_EMAIL]: "/autentificare/email",
        [PAGES.VERIFICARE_COD]: "/cod-de-acces/verificare",
        [PAGES.INTRARE_COD_SCOALA]: "/colaborator/cod",

        [PAGES.LESSON_COMPLETION]: "/completare-lectie/id/:id",
        [PAGES.LESSON_COMPLETION_PREVIEW]: "/completare-lectie/id/:id/preview",

        [PAGES.HOMEWORK_COMPLETION]: "/completare-exercitiu-pentru-acasa/id/:id",
        [PAGES.HOMEWORK_COMPLETION_PREVIEW]: "/completare-exercitiu-pentru-acasa/id/:id/preview",

        [PAGES.PRACTICAL_ACTIVITY_COMPLETION]: "/completare-activitate-practica/id/:id",
        [PAGES.PRACTICAL_ACTIVITY_COMPLETION_PREVIEW]: "/completare-activitate-practica/id/:id/preview",

        [PAGES.MONTHLY_CHALLENGES_COMPLETION]: "/completare-provocare/id/:id",
        [PAGES.MONTHLY_CHALLENGES_COMPLETION_PREVIEW]: "/completare-provocare/id/:id/preview",

        // [PAGES.DAILY_CHALLENGES_COMPLETION]: "/completare-provocare-zilnica/id/:id",
        // [PAGES.DAILY_CHALLENGES_COMPLETION_PREVIEW]: "/completare-provocare-zilnica/id/:id/preview",

        [PAGES.SCHOOLS_REPORT]: "/rapoarte/scoli",
        [PAGES.SCHOOLS_GRAPHIC_REPORT]: "/rapoarte/grafic-activitati",
        [PAGES.TOP_ELEVI_REPORT]: "/rapoarte/top-elevi",
        [PAGES.COMPLETED_QUIZZES]: "/rapoarte/activitati",
        [PAGES.NOTIFICATIONS_REPORT]: "/rapoarte/notificari",
        [PAGES.SCHOOL_REQUEST_FORM]: "/cerere-inscriere-scoala",
        [PAGES.SCHOOL_FORM_V2]: "/actualizare-detalii-scoala",
        [PAGES.PARENTAL_APPROVAL]: "/acord-parinti",
        [PAGES.FINAL_REPORT]: "/rapoarte/editie",
        [PAGES.PUPILS_REPORT]: "/rapoarte/conturi-elevi",
        [PAGES.USERS_REPORT]: "/rapoarte/utilizatori",
        [PAGES.SCHOOL_REGISTRATION_CONFIRMATION]: "/confirmare-inscriere-scoala",
        [PAGES.COLLABORATOR_LINKS]: "/colaboratori",
        [PAGES.COLLAB_ACTIVITY]: "/activitate/:token",
    });

    const NAV_LINKS_V2: NavLinksType = Object.freeze({
        [PAGES.HOME]: {
            ALLOWED_ROLES: [],
            PATH: PATHS[PAGES.HOME],
            component: HomePage,
            lazy: false, // Critical route - load immediately
            TEXT: "Despre Scoala Ecoterrienilor",
            layout: 'public',
            showInSidebar: false,
        },
        /* «Acasa»: tinta implicita dupa login pentru PROFESOR aprobat si ELEV activ (`homePathForRole`).
           Primul din «Activitati» (`menuOrder: 0.5` — `0` ar fi tratat ca 999 de sortare). Ramane in
           `activities`, nu intr-un grup nou, ca sa nu se atinga `AppSidebar.tsx` (ramura grupurilor de meniu).
           `SHOW_CONDITION`: elevul neactivat si profesorul fara scoala au alte tinte (gardele resolverului),
           deci nu li se arata linkul. */
        [PAGES.ACASA]: {
            ALLOWED_ROLES: [ROLES.PROFESOR, ROLES.ELEV],
            PATH: PATHS[PAGES.ACASA],
            component: AcasaPage,
            lazy: true,
            menuOrder: 0.5,
            menuGroup: 'activities',
            TEXT: "Acasă",
            layout: 'authenticated',
            showInSidebar: true,
            SHOW_CONDITION: (userInfo) =>
                userInfo?.role === 'ELEV' ? userInfo?.isEnabled === true : Boolean(userInfo?.schoolId),
        },
        [PAGES.LOGIN]: {
            ALLOWED_ROLES: [],
            PATH: PATHS[PAGES.LOGIN],
            component: LoginPage, // No forRole prop - unified login for all users
            lazy: false, // Critical route - load immediately
            TEXT: "Autentificare",
            layout: 'public',
            showInSidebar: false,
        },
        /* Autentificarea cu cod (U2). Inregistrate DOAR cu flagul pornit: fara el
           rutele nu exista, deci catch-all-ul din `main.jsx` le trimite la
           autentificare. Ecrane publice (`ALLOWED_ROLES: []`, layout public), deci
           resolverul post-login nu are ce decide pentru ele.
           ⚠️ Caile sunt `/cod-de-acces*`, deliberat departe de `/autentificare` si de
           redirecturile mostenite `/profesor/autentificare` si `/elev/autentificare`. */
        ...(AUTENTIFICARE_COD_ACTIVA ? {
            /* Pasul 2 al «Autentificare» (21.09): alegerea sta pe `/autentificare`, emailul aici. */
            [PAGES.LOGIN_EMAIL]: {
                ALLOWED_ROLES: [],
                PATH: PATHS[PAGES.LOGIN_EMAIL],
                component: <AutentificareEmailPage />,
                TEXT: "Autentificare",
                layout: 'public',
                showInSidebar: false,
            },
            [PAGES.CERERE_COD]: {
                ALLOWED_ROLES: [],
                PATH: PATHS[PAGES.CERERE_COD],
                component: <CerereCodPage />,
                TEXT: "Cod de acces",
                layout: 'public',
                showInSidebar: false,
            },
        } : {}),
        /* VERIFICARE_COD e comuna la doua fluxuri (autentificare cu cod SI wizardul
           de inscriere, p8): se inregistreaza sub ORICARE dintre cele doua flag-uri,
           nu doar sub cel de autentificare -- altfel `INREGISTRARE_COD_ACTIV` pornit
           singur ar trimite dupa "Trimite-mi codul" pe o ruta inexistenta. */
        ...(AUTENTIFICARE_COD_ACTIVA || INREGISTRARE_COD_ACTIV ? {
            [PAGES.VERIFICARE_COD]: {
                ALLOWED_ROLES: [],
                PATH: PATHS[PAGES.VERIFICARE_COD],
                component: <VerificareCodPage />,
                TEXT: "Verificare cod",
                layout: 'public',
                showInSidebar: false,
            },
        } : {}),
        /* Intrarea cu codul scolii (SP-05b). Inregistrata DOAR sub flag, acelasi tipar ca mai sus.
           Publica: cine intra pe aici nu are cont, si tocmai asta e ideea.
           ⚠️ Calea e `/colaborator/cod`, nu `/cod-de-acces*`: cele doua intrari raspund la intrebari
           diferite — OTP-ul te intreaba CINE ESTI ca sa te bage in contul TAU, codul scolii te leaga
           de o SCOALA fara sa ai cont. Cai vecine ar fi sugerat ca sunt doua variante ale
           aceluiasi lucru. */
        ...(INTRARE_COD_SCOALA_ACTIVA ? {
            [PAGES.INTRARE_COD_SCOALA]: {
                ALLOWED_ROLES: [],
                PATH: PATHS[PAGES.INTRARE_COD_SCOALA],
                component: <CodScoalaPage />,
                TEXT: "Intrare cu codul scolii",
                layout: 'public',
                showInSidebar: false,
            },
        } : {}),
        [PAGES.TEACHER_AUTH]: {
            ALLOWED_ROLES: [],
            PATH: PATHS[PAGES.TEACHER_AUTH],
            component: () => <Navigate to={PATHS[PAGES.LOGIN]} replace />, // Deprecated - redirects to unified login
            lazy: true, // Lazy load to avoid router context issues
            TEXT: "Autentificare Profesor (Redirect)",
            layout: 'public',
            showInSidebar: false,
        },
        [PAGES.TEACHER_REGISTER]: {
            ALLOWED_ROLES: [],
            PATH: PATHS[PAGES.TEACHER_REGISTER],
            // p8: acelasi PATH comuta componenta sub VITE_INREGISTRARE_COD -- fara
            // flag, formularul clasic cu parola ramane IDENTIC cu PROD.
            component: INREGISTRARE_COD_ACTIV
                ? <InregistrareCodPage />
                : <RegisterPage forRole={ROLES.PROFESOR} />,
            TEXT: "Inregistrare",
            layout: 'public',
            showInSidebar: false,
        },
        [PAGES.PUPIL_AUTH]: {
            ALLOWED_ROLES: [],
            PATH: PATHS[PAGES.PUPIL_AUTH],
            component: () => <Navigate to={PATHS[PAGES.LOGIN]} replace />, // Deprecated - redirects to unified login
            lazy: true, // Lazy load to avoid router context issues
            TEXT: "Autentificare Elev (Redirect)",
            layout: 'public',
            showInSidebar: false,
        },
        [PAGES.PUPIL_REGISTER]: {
            ALLOWED_ROLES: [],
            PATH: PATHS[PAGES.PUPIL_REGISTER],
            /* V1c: aceeasi cale comuta componenta sub `VITE_AUTH_PAROLA`, exact ca la
               TEACHER_REGISTER sub `VITE_INREGISTRARE_COD`. Cu parola PORNITA ramane formularul de
               azi, identic. Comutarea se face pe flagul de PAROLA, nu pe cel de inscriere cu cod:
               intrebarea la care raspunde ecranul e „mai exista parola?", nu „exista wizardul?". */
            component: AUTH_PAROLA_ACTIVA
                ? <RegisterPage forRole={ROLES.ELEV} />
                : <InregistrareElevCodPage />,
            TEXT: "Inregistrare",
            layout: 'public',
            showInSidebar: false,
        },
        /* Reinscrierea elevului cerea PAROLA contului vechi, deci nu are ce cauta intr-o lume fara
           parole: din V1c, elevul din editia trecuta se inscrie pe calea cu cod, care ii face cont
           nou in editia curenta (`OtpService.contPentruInscriereElev`). Ruta ramane inregistrata
           doar cu parola pornita — altfel un link vechi ar duce la un formular care cere ceva ce
           nu mai exista. */
        ...(AUTH_PAROLA_ACTIVA ? {
            [PAGES.PUPIL_REENROLL]: {
                // Public: elevul din editia trecuta nu are inca token pe editia curenta.
                ALLOWED_ROLES: [],
                PATH: PATHS[PAGES.PUPIL_REENROLL],
                component: <PupilReenrollPage />,
                TEXT: "Reînscriere elev",
                layout: 'public',
                showInSidebar: false,
            },
        } : {}),
        /* Recuperarea parolei, sub `VITE_AUTH_PAROLA` (oprit implicit din 21.09): fara flag
           rutele nu se inregistreaza deloc, deci catch-all-ul din `main.jsx` le trimite la
           autentificare. Acelasi tipar ca la rutele de cod, si din acelasi motiv: un ecran de
           parola atins printr-un link vechi ar contrazice pe loc «nu mai exista parola». */
        ...(AUTH_PAROLA_ACTIVA ? {
            [PAGES.FORGOTTEN_PASSWORD]: {
                ALLOWED_ROLES: [],
                PATH: PATHS[PAGES.FORGOTTEN_PASSWORD],
                component: ForgottenPasswordPage,
                lazy: false, // Critical route - load immediately
                TEXT: "Am uitat parola",
                layout: 'public',
                showInSidebar: false,
            },
            [PAGES.NEW_PASS_FOR_FORGOTTEN_PASSWORD]: {
                ALLOWED_ROLES: [],
                PATH: PATHS[PAGES.NEW_PASS_FOR_FORGOTTEN_PASSWORD],
                component: NewPasswordPage,
                lazy: false, // Critical route - load immediately
                TEXT: "Parola Noua",
                layout: 'public',
                showInSidebar: false,
            },
        } : {}),
        [PAGES.LESSON_QUIZ_LISTING]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.LESSON_QUIZ_LISTING],
            component: <LessonSystemRouter action="list" lessonType={LessonType.LESSON} />,
            lazy: false, // Has props - cannot lazy load JSX with props
            menuOrder: 1, // Activities: Lectii
            menuGroup: 'activities',
            TEXT: "Lectii",
            layout: 'authenticated',
            showInSidebar: true,
            SHOW_CONDITION: () => isFeatureEnabled('LESSONS'),
        },
        [PAGES.LESSON_QUIZ_CREATION]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.LESSON_QUIZ_CREATION],
            component: <LessonSystemRouter action="create" lessonType={LessonType.LESSON} />,
            TEXT: "Adaugare lectie",
            layout: 'authenticated',
            showInSidebar: false,
            SHOW_CONDITION: () => isFeatureEnabled('LESSONS'),
        },
        [PAGES.HOMEWORK_LISTING]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR, ROLES.ELEV],
            PATH: PATHS[PAGES.HOMEWORK_LISTING],
            component: <LessonSystemRouter action="list" lessonType={LessonType.HOMEWORK} />,
            lazy: false, // Has props - cannot lazy load JSX with props
            menuOrder: 2, // Activities: Exercitii Pentru Acasa
            menuGroup: 'activities',
            TEXT: "Exercitii Pentru Acasa",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.HOMEWORK_CREATION]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.HOMEWORK_CREATION],
            component: <LessonSystemRouter action="create" lessonType={LessonType.HOMEWORK} />,
            TEXT: "Adaugare Tema de Casa",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.PRACTICAL_ACTIVITY_LISTING]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.PRACTICAL_ACTIVITY_LISTING],
            component: <LessonSystemRouter action="list" lessonType={LessonType.PRACTICAL_ACTIVITY} />,
            lazy: false, // Has props - cannot lazy load JSX with props
            menuOrder: 4, // Activities: Activitati Practice
            menuGroup: 'activities',
            TEXT: "Activitati Practice",
            layout: 'authenticated',
            showInSidebar: true,
            IS_BOLD: true,
        },
        [PAGES.PRACTICAL_ACTIVITY_CREATION]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.PRACTICAL_ACTIVITY_CREATION],
            component: <LessonSystemRouter action="create" lessonType={LessonType.PRACTICAL_ACTIVITY} />,
            TEXT: "Adaugare Tema de Casa",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.PRACTICAL_ACTIVITY_DETAILS]: {
            // B3: „Detalii" — citire pentru toti cei care vad lista; „Încarcă poze" e doar PROFESOR.
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.PRACTICAL_ACTIVITY_DETAILS],
            component: PracticalDetailsPage,
            lazy: true,
            TEXT: "Detalii activitate practică",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.PRACTICAL_ACTIVITY_EVIDENCE]: {
            // B3: „Încarcă poze" — DOAR PROFESOR: ADMIN/DBEST n-au scoala in JWT, deci n-au ce depune (decis 24.09).
            ALLOWED_ROLES: [ROLES.PROFESOR],
            PATH: PATHS[PAGES.PRACTICAL_ACTIVITY_EVIDENCE],
            component: PracticalEvidencePage,
            lazy: true,
            TEXT: "Încarcă poze activitate practică",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.SCHOOL_CHALLENGE_LISTING]: {
            // Provocari pentru scoli: le depune profesorul, le aproba ADMIN/DBEST. Elevii nu le vad.
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.SCHOOL_CHALLENGE_LISTING],
            component: <LessonSystemRouter action="list" lessonType={LessonType.SCHOOL_CHALLENGE} />,
            lazy: false, // Has props - cannot lazy load JSX with props
            menuOrder: 4.5, // Activities: imediat dupa Activitati Practice
            menuGroup: 'activities',
            TEXT: "Provocari pentru Scoli",
            layout: 'authenticated',
            showInSidebar: true,
            IS_BOLD: true,
        },
        [PAGES.SCHOOL_CHALLENGE_CREATION]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.SCHOOL_CHALLENGE_CREATION],
            component: <LessonSystemRouter action="create" lessonType={LessonType.SCHOOL_CHALLENGE} />,
            TEXT: "Adaugare Provocare pentru Scoli",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.SCHOOL_CHALLENGE_DETAILS]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.SCHOOL_CHALLENGE_DETAILS],
            component: <PracticalDetailsPage tip="SCHOOL_CHALLENGE" />,
            lazy: false, // Has props - cannot lazy load JSX with props
            TEXT: "Detalii provocare pentru școală",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.SCHOOL_CHALLENGE_EVIDENCE]: {
            // DOAR PROFESOR: ADMIN/DBEST n-au scoala in JWT, deci n-au ce depune.
            ALLOWED_ROLES: [ROLES.PROFESOR],
            PATH: PATHS[PAGES.SCHOOL_CHALLENGE_EVIDENCE],
            component: <PracticalEvidencePage tip="SCHOOL_CHALLENGE" />,
            lazy: false, // Has props - cannot lazy load JSX with props
            TEXT: "Încarcă dovezi provocare pentru școală",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.COLLABORATOR_LINKS]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.COLLABORATOR_LINKS],
            component: CollaboratorLinksPage,
            lazy: true,
            menuOrder: 6, // Activities: Colaboratori
            menuGroup: 'activities',
            TEXT: "Colaboratori",
            layout: 'authenticated',
            showInSidebar: true,
            // D3 (fix round 1): schoolId ajunge in JWT doar cand userul are scoala - ADMIN/DBEST
            // nu au. Vizibilitatea in meniu se restrange la cine chiar poate folosi pagina asa;
            // ALLOWED_ROLES ramane neatins, accesul direct prin URL pentru ADMIN/DBEST e valid.
            SHOW_CONDITION: (userInfo) => userInfo?.role === 'PROFESOR' && Boolean(userInfo?.isApproved && userInfo?.schoolId),
        },
        [PAGES.MONTHLY_CHALLENGES_LISTING]: {
            ALLOWED_ROLES: [ROLES.ADMIN, ROLES.ELEV, ROLES.PROFESOR],
            PATH: PATHS[PAGES.MONTHLY_CHALLENGES_LISTING],
            component: <LessonSystemRouter action="list" lessonType={LessonType.MONTHLY_CHALLENGE} />,
            lazy: false, // Has props - cannot lazy load JSX with props
            menuOrder: 5, // Activities: Provocari
            menuGroup: 'activities',
            TEXT: "Provocari",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.MONTHLY_CHALLENGES_CREATION]: {
            ALLOWED_ROLES: [ROLES.ADMIN, ROLES.ELEV, ROLES.PROFESOR],
            PATH: PATHS[PAGES.MONTHLY_CHALLENGES_CREATION],
            component: <LessonSystemRouter action="create" lessonType={LessonType.MONTHLY_CHALLENGE} />,
            TEXT: "Adaugare Provocare",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.CLASS_EXERCISE_LISTING]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.CLASS_EXERCISE_LISTING],
            component: <LessonSystemRouter action="list" lessonType={LessonType.CLASS_EXERCISE} />,
            lazy: false, // Has props - cannot lazy load JSX with props
            menuOrder: 3, // Activities: Exercitii Pentru Clasa
            menuGroup: 'activities',
            TEXT: "Exercitii Pentru Clasa",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.CLASS_EXERCISE_CREATION]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.CLASS_EXERCISE_CREATION],
            component: <LessonSystemRouter action="create" lessonType={LessonType.CLASS_EXERCISE} />,
            TEXT: "Adaugare Exercitiu in clasa",
            layout: 'authenticated',
            showInSidebar: false,
        },

        // [PAGES.DAILY_CHALLENGES_LISTING]: {
        //     ALLOWED_ROLES: [ROLES.ADMIN, ROLES.ELEV],
        //     PATH: PATHS[PAGES.DAILY_CHALLENGES_LISTING],
        //     component: <DailyChallengesListing quizType={QUIZ_TYPES.DAILY_CHALLENGE} />,
        //     TEXT: "Provocari Zilnice",
        // },
        // [PAGES.DAILY_CHALLENGES_CREATION]: {
        //     ALLOWED_ROLES: [ROLES.ADMIN, ROLES.ELEV],
        //     PATH: PATHS[PAGES.DAILY_CHALLENGES_CREATION],
        //     component: <QuizCreation quizType={QUIZ_TYPES.DAILY_CHALLENGE} />,
        //     TEXT: "Adaugare Provocare Zilnica",
        // },
        // [PAGES.DAILY_CHALLENGES_CREATION_REDIRECT]: {
        //     ALLOWED_ROLES: [ROLES.ADMIN, ROLES.ELEV],
        //     PATH: PATHS[PAGES.DAILY_CHALLENGES_CREATION_REDIRECT],
        //     component: <QuizCreation quizType={QUIZ_TYPES.DAILY_CHALLENGE} />,
        //     TEXT: "Adaugare Provocare Zilnica",
        // },

        [PAGES.WASTE_COLLECTION_FORM]: {
            ALLOWED_ROLES: [ROLES.PROFESOR],
            PATH: PATHS[PAGES.WASTE_COLLECTION_FORM],
            component: WasteCollectionFormPage,
            lazy: true, // Form page - lazy load
            menuOrder: 6.1, // Forms: Plasare Comanda (right after Raport Comenzi)
            menuGroup: 'waste',
            TEXT: "Plasare Comanda",
            layout: 'authenticated',
            showInSidebar: true,
        },
        /* «Comandă recipiente DBA» (SP-04). Se inregistreaza DOAR cu flagul pornit, ca
           pasul de video si ca protocolul: fara flag ruta nu exista, deci nici
           intrarea de meniu n-are unde sa duca — o legatura randata ar cadea pe
           catch-all-ul din `main.jsx` si ar arata identic cu o sesiune expirata.

           🔴 Eticheta a fost «Cere recipiente» (meniul are trei intrari cu „comandă/
           comenzi" care inseamna PREDAU deseuri). Din 2026-09-21, la cererea ECOTIC,
           e «Comandă recipiente DBA» — sufixul «DBA» o deosebeste. Calea ramane
           `/cere-recipiente`, ca linkurile deja trimise sa nu se rupa. */
        ...(CERE_RECIPIENTE_ACTIV ? {
            [PAGES.CERE_RECIPIENTE]: {
                ALLOWED_ROLES: [ROLES.PROFESOR],
                PATH: PATHS[PAGES.CERE_RECIPIENTE],
                component: CereRecipientePage,
                lazy: true,
                menuOrder: 6.3, // intre «Plasare Comanda» (6.1) si «Puncte si tichete» (6.5)
                menuGroup: 'waste',
                TEXT: "Comandă recipiente DBA",
                layout: 'authenticated',
                showInSidebar: true,
            },
            /* Lista de lucru a adminului, sub ACELASI flag: fara ecranul
               profesorului nu exista cereri de gestionat, deci un tabel gol si o
               intrare de meniu care nu duce nicaieri. */
            [PAGES.RECIPIENTE_ADMIN]: {
                // SE-18: LOGISTIC adaugat -- backendul (GET /report) ii da deja acces, ca sa
                // poata marca livrarile; fara randul asta ruta l-ar respinge inainte sa ajunga
                // vreodata la butonul "Marchează livrată".
                ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.LOGISTIC],
                PATH: PATHS[PAGES.RECIPIENTE_ADMIN],
                component: RecipienteAdminPage,
                lazy: true,
                menuOrder: 9.9,
                menuGroup: 'admin',
                TEXT: "Comenzi recipiente DBA",
                layout: 'authenticated',
                showInSidebar: true,
            },
        } : {}),
        [PAGES.TICKET_TIERS_ADMIN]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.TICKET_TIERS_ADMIN],
            component: TicketTiersAdminPage,
            lazy: true,
            menuOrder: 9.7,
            menuGroup: 'admin',
            TEXT: "Niveluri premii",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.TICKET_REDEMPTIONS_REPORT]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.TICKET_REDEMPTIONS_REPORT],
            component: TicketRedemptionsReportPage,
            lazy: true,
            menuOrder: 9.8,
            menuGroup: 'admin',
            TEXT: "Revendicari premii",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.SCHOOL_POINTS]: {
            ALLOWED_ROLES: [ROLES.PROFESOR],
            PATH: PATHS[PAGES.SCHOOL_POINTS],
            component: SchoolPointsPage,
            lazy: true,
            menuOrder: 6.5, // dupa Plasare Comanda
            menuGroup: 'waste',
            TEXT: "Puncte si premii",
            layout: 'authenticated',
            showInSidebar: true,
            // P1-13: un PROFESOR cu cererea inca PENDING n-are schoolId in JWT, iar
            // pagina cere GET /api/schools/own/points-balance -> 409 "lipsesc datele
            // de scoala/editie" (acelasi tipar ca P0-4). Masurat pe DEV: 0 din cele 12
            // intrari de sidebar dau 403 de rol -- restrictia reala vine din 409, nu
            // din SecurityConfiguration, deci nu se ascunde dupa rol, ci dupa
            // schoolId, la fel ca la Colaboratori (D3) mai sus. Doar PROFESOR are
            // JWT-ul asta; alte roluri trec neconditionat.
            SHOW_CONDITION: (userInfo) => userInfo?.role !== 'PROFESOR' || Boolean(userInfo?.schoolId),
        },
        [PAGES.WASTE_COLLECTION_TABLE]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR, ROLES.LOGISTIC],
            PATH: PATHS[PAGES.WASTE_COLLECTION_TABLE],
            component: WasteOrdersReportPage,
            lazy: true, // Report page - lazy load
            menuOrder: 6, // Reports: Raport Comenzi
            menuGroup: 'waste',
            TEXT: "Raport Comenzi",
            layout: 'authenticated',
            showInSidebar: true,
            // P1-13: acelasi 409 "lipsesc datele de scoala/editie" pe GET
            // /api/waste-collection-forms/report, pentru un PROFESOR fara schoolId
            // (cerere PENDING). DBEST/ADMIN/LOGISTIC nu au schoolId propriu si nu
            // trebuie afectati -- conditia se aplica DOAR rolului PROFESOR.
            SHOW_CONDITION: (userInfo) => userInfo?.role !== 'PROFESOR' || Boolean(userInfo?.schoolId),
        },
        [PAGES.WASTE_COLLECTION_REJECTED_TABLE]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.WASTE_COLLECTION_REJECTED_TABLE],
            component: RejectedWasteOrdersReportPage,
            lazy: true, // Report page - lazy load
            menuOrder: 6.2, // Reports: Raport Comenzi Respinse
            menuGroup: 'waste',
            TEXT: "Raport Comenzi Respinse",
            layout: 'authenticated',
            showInSidebar: true,
            // P1-13: acelasi endpoint de raport ca mai sus (filtrat pe status) -> acelasi 409.
            SHOW_CONDITION: (userInfo) => userInfo?.role !== 'PROFESOR' || Boolean(userInfo?.schoolId),
        },
        [PAGES.MESSAGES]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR, ROLES.ELEV],
            PATH: PATHS[PAGES.MESSAGES],
            component: MessagesPage,
            lazy: true, // Message page - lazy load
            menuOrder: 13, // Comunicare: Mesaje (era «admin»: eticheta «Instrumente Admin» minte pentru ELEV/PROFESOR)
            menuGroup: 'comunicare',
            TEXT: "Mesaje",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.PROFILE]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR, ROLES.ELEV, ROLES.LOGISTIC],
            PATH: PATHS[PAGES.PROFILE],
            component: ProfilePage,
            lazy: false,
            TEXT: "Profil",
            layout: 'authenticated',
            showInSidebar: false,
        },
        /* Schimbarea parolei, sub acelasi flag.
           ⚠️ `NavUser` citea `NAV_LINKS_V2[PAGES.PASSWORD_CHANGE].PATH` neconditionat — cu ruta
           scoasa de aici, aia devine `undefined.PATH`, adica o EXCEPTIE la randarea meniului de
           utilizator, nu un link mort. Garda e in `NavUser`, in acelasi commit.
           ⚠️ `PATHS[PAGES.PASSWORD_CHANGE]` ramane definit (harta `PATHS` e separata), deci linia
           din `resolve-post-auth-target.ts` care il pune in `allowedPaths` ramane INERTA, exact ca
           cele pentru `VIDEO_INSCRIERE` si `PROTOCOL_INSCRIERE`. Resolverul ramane functie pura,
           fara flaguri de build citite in el. */
        ...(AUTH_PAROLA_ACTIVA ? {
            [PAGES.PASSWORD_CHANGE]: {
                ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR, ROLES.ELEV, ROLES.LOGISTIC],
                PATH: PATHS[PAGES.PASSWORD_CHANGE],
                component: PasswordChangePage,
                lazy: true,
                TEXT: "Schimbă Parola",
                layout: 'authenticated',
                showInSidebar: false,
            },
        } : {}),
        [PAGES.ACCOUNTS_AUTHORIZATION]: {
            ALLOWED_ROLES: [ROLES.ADMIN],
            PATH: PATHS[PAGES.ACCOUNTS_AUTHORIZATION],
            component: SchoolRequestsReportPage,
            lazy: true, // Report page - lazy load
            menuOrder: 11, // Admin: Raport Inscrieri
            menuGroup: 'admin',
            TEXT: "Raport Inscrieri",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.PUPILS_REPORT]: {
            ALLOWED_ROLES: [ROLES.PROFESOR],
            PATH: PATHS[PAGES.PUPILS_REPORT],
            component: <UsersReportPage initialFilter={{ role: ROLES.ELEV }} simplifiedView={true} />,
            lazy: false, // Has props - cannot lazy load JSX with props
            menuOrder: 8, // Users: Raport Conturi Elevi (Elevii mei)
            menuGroup: 'users',
            TEXT: "Raport Conturi Elevi",
            layout: 'authenticated',
            showInSidebar: true,
            // P1-13: acelasi 409 pe GET /api/users/report?role=ELEV, pentru un
            // PROFESOR fara schoolId (cerere PENDING). ALLOWED_ROLES e doar
            // PROFESOR aici, dar conditia ramane explicita pe rol pentru
            // consistenta cu celelalte trei intrari din acelasi fix.
            SHOW_CONDITION: (userInfo) => userInfo?.role !== 'PROFESOR' || Boolean(userInfo?.schoolId),
        },
        [PAGES.ACCOUNT_UNAUTHORIZED_TEACHER]: {
            ALLOWED_ROLES: [ROLES.PROFESOR],
            PATH: PATHS[PAGES.ACCOUNT_UNAUTHORIZED_TEACHER],
            component: <AccountWaitingPage role={ROLES.PROFESOR} />,
            TEXT: "Cont in asteptare",
            layout: 'authenticated',
            showInSidebar: false,
        },
        /* Pasul de video din inscriere (U4), pe fluxul scris al clientului: dupa
           reconfirmarea datelor, inainte de „Genereaza protocol". Se inregistreaza
           DOAR cu flagul pornit — fara el ruta nu exista, deci catch-all-ul din
           `main.jsx` o trimite la autentificare, iar intrarea din lista de cai
           permise a resolverului n-are ce sa deschida. */
        ...(PAS_VIDEO_ACTIV ? {
            [PAGES.VIDEO_INSCRIERE]: {
                ALLOWED_ROLES: [ROLES.PROFESOR],
                PATH: PATHS[PAGES.VIDEO_INSCRIERE],
                component: <VideoInscrierePage />,
                TEXT: "Material video",
                layout: 'authenticated',
                showInSidebar: false,
            },
        } : {}),
        /* Pasul 4 din 4: protocolul de colaborare (SP-03). Acelasi tipar ca pasul
           de video de mai sus — inregistrata DOAR sub flag, ca sa nu existe o ruta
           moarta si o legatura care duce la autentificare. */
        ...(PROTOCOL_DESCARCARE_ACTIV ? {
            [PAGES.PROTOCOL_INSCRIERE]: {
                ALLOWED_ROLES: [ROLES.PROFESOR],
                PATH: PATHS[PAGES.PROTOCOL_INSCRIERE],
                component: <ProtocolPage />,
                TEXT: "Protocol de colaborare",
                layout: 'authenticated',
                showInSidebar: false,
            },
        } : {}),
        [PAGES.PARENTAL_APPROVAL]: {
            ALLOWED_ROLES: [ROLES.ELEV],
            PATH: PATHS[PAGES.PARENTAL_APPROVAL],
            component: ParentalApprovalPage,
            lazy: true, // Form page - lazy load
            menuOrder: 17, // Admin: Acord Parinti (Conditional)
            menuGroup: 'formulare',
            TEXT: "Acord Parinti",
            layout: 'authenticated',
            showInSidebar: true,
            // Show only for ELEV users who haven't completed parental approval (not enabled yet)
            SHOW_CONDITION: (userInfo) => !userInfo?.isEnabled,
        },
        [PAGES.LESSON_COMPLETION]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.LESSON_COMPLETION],
            component: <LessonSystemRouter action="complete" lessonType={LessonType.LESSON} />,
            TEXT: "Completare lectie",
            layout: 'authenticated',
            showInSidebar: false,
            SHOW_CONDITION: () => isFeatureEnabled('LESSONS'),
        },
        [PAGES.LESSON_COMPLETION_PREVIEW]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.LESSON_COMPLETION_PREVIEW],
            component: <LessonSystemRouter action="complete" lessonType={LessonType.LESSON} />,
            TEXT: "Completare lectie",
            layout: 'authenticated',
            showInSidebar: false,
            SHOW_CONDITION: () => isFeatureEnabled('LESSONS'),
        },
        [PAGES.HOMEWORK_COMPLETION]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR, ROLES.ELEV],
            PATH: PATHS[PAGES.HOMEWORK_COMPLETION],
            component: <LessonSystemRouter action="complete" lessonType={LessonType.HOMEWORK} />,
            TEXT: "Completare tema pentru acasa",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.HOMEWORK_COMPLETION_PREVIEW]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR, ROLES.ELEV],
            PATH: PATHS[PAGES.HOMEWORK_COMPLETION_PREVIEW],
            component: <LessonSystemRouter action="complete" lessonType={LessonType.HOMEWORK} />,
            TEXT: "Completare tema pentru acasa",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.PRACTICAL_ACTIVITY_COMPLETION]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.PRACTICAL_ACTIVITY_COMPLETION],
            component: <LessonSystemRouter action="complete" lessonType={LessonType.PRACTICAL_ACTIVITY} />,
            TEXT: "Completare activitate practica",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.PRACTICAL_ACTIVITY_COMPLETION_PREVIEW]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.PRACTICAL_ACTIVITY_COMPLETION_PREVIEW],
            component: <LessonSystemRouter action="complete" lessonType={LessonType.PRACTICAL_ACTIVITY} />,
            TEXT: "Completare activitate practica",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.MONTHLY_CHALLENGES_COMPLETION]: {
            // DBEST adaugat 2026-09-06: permisiunea pe TIP il lasa sa completeze provocarea
            // lunara (`complete: [ADMIN, DBEST, ELEV]`), dar ruta il oprea — singura din cele
            // cinci rute de completare care nu-l avea. Vezi `roluri-completare.test.ts`.
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.ELEV, ROLES.PROFESOR],
            PATH: PATHS[PAGES.MONTHLY_CHALLENGES_COMPLETION],
            component: <LessonSystemRouter action="complete" lessonType={LessonType.MONTHLY_CHALLENGE} />,
            TEXT: "Completare Provocare",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.MONTHLY_CHALLENGES_COMPLETION_PREVIEW]: {
            ALLOWED_ROLES: [ROLES.ADMIN, ROLES.ELEV, ROLES.PROFESOR],
            PATH: PATHS[PAGES.MONTHLY_CHALLENGES_COMPLETION_PREVIEW],
            component: <LessonSystemRouter action="complete" lessonType={LessonType.MONTHLY_CHALLENGE} />,
            TEXT: "Completare Provocare",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.CLASS_EXERCISE_COMPLETION]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.CLASS_EXERCISE_COMPLETION],
            component: <LessonSystemRouter action="complete" lessonType={LessonType.CLASS_EXERCISE} />,
            TEXT: "Completare exercitiu in clasa",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.CLASS_EXERCISE_COMPLETION_PREVIEW]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.CLASS_EXERCISE_COMPLETION_PREVIEW],
            component: <LessonSystemRouter action="complete" lessonType={LessonType.CLASS_EXERCISE} />,
            TEXT: "Completare exercitiu in clasa",
            layout: 'authenticated',
            showInSidebar: false,
        },

        // [PAGES.DAILY_CHALLENGES_COMPLETION]: {
        //     ALLOWED_ROLES: [ROLES.ADMIN, ROLES.ELEV],
        //     PATH: PATHS[PAGES.DAILY_CHALLENGES_COMPLETION],
        //     component: <QuizCompletion quizType={QUIZ_TYPES.DAILY_CHALLENGE} />,
        //     TEXT: "Completare Provocare Zilnica",
        // },
        // [PAGES.DAILY_CHALLENGES_COMPLETION_PREVIEW]: {
        //     ALLOWED_ROLES: [ROLES.ADMIN, ROLES.ELEV],
        //     PATH: PATHS[PAGES.DAILY_CHALLENGES_COMPLETION_PREVIEW],
        //     component: <QuizCompletion quizType={QUIZ_TYPES.DAILY_CHALLENGE} />,
        //     TEXT: "Completare Provocare Zilnica",
        // },

        [PAGES.SCHOOLS_REPORT]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.SCHOOLS_REPORT],
            component: SchoolsReportPage,
            lazy: true, // Report page - lazy load
            menuOrder: 9, // Admin: Raport Scoli
            menuGroup: 'admin',
            TEXT: "Raport Scoli",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.SCHOOLS_GRAPHIC_REPORT]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.SCHOOLS_GRAPHIC_REPORT],
            component: SchoolsGraphicReportPage,
            lazy: true, // Chart page - lazy load
            menuOrder: 9.5, // Admin: Grafic Activitati (after Raport Scoli)
            menuGroup: 'admin',
            TEXT: "Grafic Activități",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.TOP_ELEVI_REPORT]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.TOP_ELEVI_REPORT],
            component: StudentsLeaderboardReportPage,
            lazy: true, // Report page - lazy load
            menuOrder: 9.6, // Admin: Top Elevi (right after Grafic Activitati)
            menuGroup: 'admin',
            TEXT: "Top Elevi",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.COMPLETED_QUIZZES]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN, ROLES.PROFESOR],
            PATH: PATHS[PAGES.COMPLETED_QUIZZES],
            component: LessonAnswersReportPage,
            lazy: true, // Report page - lazy load
            menuOrder: 7, // Activities: Raport Activitati
            menuGroup: 'activities',
            TEXT: "Raport Activitati",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.NOTIFICATIONS_REPORT]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.NOTIFICATIONS_REPORT],
            component: NotificationsReportPage,
            lazy: true, // Report page - lazy load
            menuOrder: 10, // Admin: Raport Notificari
            menuGroup: 'admin',
            TEXT: "Raport Notificari",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.SCHOOL_REQUEST_FORM]: {
            ALLOWED_ROLES: [ROLES.PROFESOR],
            PATH: PATHS[PAGES.SCHOOL_REQUEST_FORM],
            component: SchoolRequestFormPage,
            lazy: true, // Form page - lazy load
            menuOrder: 15, // Admin: Cerere Inscriere Scoala (Conditional)
            menuGroup: 'formulare',
            TEXT: "Cerere Inscriere Scoala",
            layout: 'authenticated',
            showInSidebar: true,
            // Show only for professors in Stage 1 or 2 (not approved)
            SHOW_CONDITION: (userInfo) => !userInfo?.isEnabled || !userInfo?.schoolId,
        },
        [PAGES.SCHOOL_FORM_V2]: {
            ALLOWED_ROLES: [ROLES.PROFESOR],
            PATH: PATHS[PAGES.SCHOOL_FORM_V2],
            component: SchoolDetailsFormPage,
            lazy: true, // Form page - lazy load
            menuOrder: 16, // Admin: Actualizare Detalii Scoala (Conditional)
            menuGroup: 'formulare',
            TEXT: "Actualizare Detalii Scoala",
            IS_BOLD: true,
            layout: 'authenticated',
            showInSidebar: true,
            // Show only for approved professors (Stage 3)
            SHOW_CONDITION: (userInfo) => userInfo?.isEnabled && userInfo?.schoolId,
        },
        [PAGES.FINAL_REPORT]: {
            ALLOWED_ROLES: [ROLES.DBEST, ROLES.ADMIN],
            PATH: PATHS[PAGES.FINAL_REPORT],
            component: EditionStatusReportPage,
            lazy: true, // Report page - lazy load
            menuOrder: 14, // Admin Tools: Raport Editie (last main item)
            menuGroup: 'admin',
            TEXT: "Raport Editie",
            layout: 'authenticated',
            showInSidebar: true,
        },
        [PAGES.SCHOOL_REGISTRATION_CONFIRMATION]: {
            ALLOWED_ROLES: [ROLES.PROFESOR],
            PATH: PATHS[PAGES.SCHOOL_REGISTRATION_CONFIRMATION],
            component: SchoolRegistrationConfirmationPage,
            lazy: true, // Form page - lazy load
            TEXT: "Confirmare inscriere scoala",
            layout: 'authenticated',
            showInSidebar: false,
        },
        [PAGES.COLLAB_ACTIVITY]: {
            // Publica: linkul de colaborator nu are cont in spate (spec §3). ALLOWED_ROLES: []
            // face ca classifyPathForRole sa intoarca 'public' - un utilizator LOGAT nu e
            // redirectat, ca sa poata testa propriul link (layout: 'open' ocoleste si
            // UnauthenticatedLayout, vezi is-path-allowed.ts si main.jsx).
            ALLOWED_ROLES: [],
            PATH: PATHS[PAGES.COLLAB_ACTIVITY],
            component: CollabActivityPage,
            lazy: true,
            TEXT: "Depunere activitate (colaborator)",
            layout: 'open',
            showInSidebar: false,
        },
        [PAGES.USERS_REPORT]: {
            ALLOWED_ROLES: [ROLES.ADMIN],
            PATH: PATHS[PAGES.USERS_REPORT],
            component: UsersReportPage,
            lazy: true, // Report page - lazy load
            menuOrder: 12, // Users: Raport Utilizatori
            menuGroup: 'users',
            TEXT: "Raport Utilizatori",
            layout: 'authenticated',
            showInSidebar: true,
        },
    });

    // Auto-generate PROFILE_LINKS from routes with showInSidebar: true
    // Sorted by menuOrder for consistent menu display
    // No manual maintenance required - adding showInSidebar: true to any route auto-adds it to menu
    const PROFILE_LINKS = Object.values(NAV_LINKS_V2)
        .filter(route => route.showInSidebar === true)
        .sort((a, b) => (a.menuOrder || 999) - (b.menuOrder || 999));

    function getListsOfPagePathsGroupedByType() {
        const unsecuredPaths: string[] = [];
        const securedPaths: string[] = [];

        Object.values(NAV_LINKS_V2).map((page) => {
            if (page.ALLOWED_ROLES.length > 0) {
                securedPaths.push(page.PATH);
            } else if (page.ALLOWED_ROLES.length === 0) unsecuredPaths.push(page.PATH);
        });

        return { unsecuredPaths, securedPaths };
    }

    // Maparea sta in `homePathForRole` (pura, testata pe functia REALA): ELEV si PROFESOR -> «Acasa»,
    // celelalte roluri neschimbate. NU e «unde ateriza userul dupa login» — asta ramane `landing()`.
    function getUserHomePage(role?: Role) {
        return homePathForRole(role, { PATHS, PAGES, ROLES });
    }

    function getMainPresentationRole(role?: Role) {
        if (role && role in ROLES_PRESENTATION_NAME) return ROLES_PRESENTATION_NAME[role];
        return "Intrus";
    }

    return (
        <Provider
            value={{
                ROLES,
                PAGES,
                PATHS,
                NAV_LINKS_V2,
                PROFILE_LINKS,
                getUserHomePage,
                ROLES_PRESENTATION_NAME,
                getMainPresentationRole,
                getListsOfPagePathsGroupedByType,
            }}>
            {children}
        </Provider>
    );
};

export { RoutesProviderContext, RoutesProvider };
